import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CellClickedEvent,
  ColDef,
  GridApi,
  SideBarDef,
} from "ag-grid-community";
import { Icon } from "@iconify/react";

import { Dialog } from "@mui/material";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import { useNavigate } from "react-router";
import { Action, CTADropdown } from "../../components/shared/Dropdown";
import { agConfigDashboardSideBar } from "../dashboardPage/AgGridConfig";

import {
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";

import { globalFilter, sort } from "../../utils/arrays";

import { Box, HStack, VStack } from "../../components/utils";
import { useAuthStore } from "../../store";
import {
  useGetOnFilterState,
  usePostOnFilterState,
} from "../dashboardPage/AgGridCacheQuery";
import GenericTableHeader from "../../shared/TableHeader";
import {
  IconCTAButton,
  PrimaryCTAButton,
} from "../quickRound/CTAButtonComponents";
import { useAgGridTableState } from "../../store/agGridTableStore";
import { tableCellStyle } from "../../components/TableComponent";
import SearchInput from "../../components/shared/SearchInput";
import { useGetUserRoleGrants } from "../../queries/auth";
import { EsopPlanDetailsModel } from "./EsopPlanDetailsAgTable";
import StakeholderEsopPopup from "./StakeholderDetailsPopup";
import { useInternalSwitchStateStore } from "../../store/searchDropDown";
import {
  useAddEsopDetails,
  useDeleteEsopDetails,
  useUpdateEsopDetails,
} from "../../queries/shareholders";
import {
  ConversionRatioRender,
  ReservedPoolRender,
  TotalSharesRender,
} from "./EsopPlanDetailsAGComponent";
import {
  AllotmentDateRender,
  shareholderNameRender,
  stakeholderNameRender,
  StakeholderNotesRender,
} from "../dashboardPage/AgGridCaptableComponent";
import { handleEventForTracking } from "../../amplitudeAnalytics";

export interface EsopStakeholderModel {
  id?: string;
  planName: string;
  reservedPool: number;
  allocatedPool: number;
  unallocatedPool: number;
  planType: string;
  conversionRatio: number;
  shareholderId?: string;
  shareholderName?: string;
  notes?: string;
}

function EsopStakeholdersAgTable({
  esopPLanDetailsList,
  shareholderId,
  currencyType,
  shareholderName,
}: {
  esopPLanDetailsList: EsopPlanDetailsModel[];
  shareholderId: string;
  currencyType: string;
  shareholderName?: string;
  notes?: string;
}) {
  const [dialog, setDialog] = useState<{
    open: boolean;
    name?: "Add" | "Update" | "Delete" | "";
    data?: any;
    mode?: string;
  }>({
    name: "",
    open: false,
  });

  const { data: userRoleData } = useGetUserRoleGrants();

  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [globalFilterText, setGlobalFilterText] = useState("");

  const gridApi = useRef<any>(null);
  const transactionSwitchStore = useInternalSwitchStateStore();
  const currencySymbol = getCurrencySymbol();
  const companyId = useAuthStore().companyId ?? "";
  const userId = useAuthStore().user?.userId ?? 0;
  const { mutate: deleteEsopDetail } = useDeleteEsopDetails();
  const { mutate: addEsopDetail } = useAddEsopDetails();
  const { mutate: updateEsopDetail } = useUpdateEsopDetails();
  const { refetch, data: filterState } = useGetOnFilterState(
    `${userId}`,
    companyId
  );
  const agTableStore = useAgGridTableState();
  const [sortField, setSortField] = useState<{
    field: keyof EsopStakeholderModel | "" | undefined;
    ascending: boolean;
  }>({ field: "planName", ascending: true });

  const displayedRowCount = gridApi.current?.api.getDisplayedRowCount();

  useEffect(() => {
    refetch().then((data) => {
      if (data.data) agTableStore.setState(data.data);
      setColumnSetting(data.data?.esopStakeholderColumnModel);
      setFilterSetting(data.data?.esopStakeholderFilterModel);
    });
  }, []);

  const { mutate: postOnFilter } = usePostOnFilterState();

  function getColumnSetting() {
    if (gridApi.current) return gridApi.current.columnApi?.getColumnState();
    return {};
  }

  function setColumnSetting(model: any) {
    if (gridApi.current)
      gridApi.current.columnApi.applyColumnState({ state: model });
    return {};
  }

  const getFilterSetting = () => {
    if (gridApi.current) return gridApi.current.api?.getFilterModel();
    return {};
  };

  const setFilterSetting = (model: any) => {
    if (gridApi.current) return gridApi.current.api?.setFilterModel(model);
    return {};
  };

  const uploadFilterAndColumn = async () => {
    const columnState = await getColumnSetting();
    const filterState = await getFilterSetting();
    postOnFilter({
      userId: `${userId}`,
      companyId,
      filterData: {
        ...agTableStore.state,
        shareholderColumnModel: columnState,
        shareholderFilterModel: filterState,
      },
    });
  };
  const componentsRegistery = useMemo(
    () => ({
      ratio: memo(ConversionRatioRender),
      reservedPool: memo(ReservedPoolRender),
      totalShares: memo(TotalSharesRender),
      stakeholderName: memo(stakeholderNameRender),
      notes: memo(StakeholderNotesRender),
      allotmentDate: memo(AllotmentDateRender),
    }),
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  );

  const navigate = useNavigate();
  const onClickShareholderName = (id: string) => {
    if (!id) return;
    handleEventForTracking({ eventName: `Shareholder Name` });
    navigate(`/shareholders/individual/${id}`, {
      state: null,
    });
  };

  const columnDefs: ColDef[] = useMemo(() => {
    const columnDef: ColDef[] = [
      {
        headerName: "Stakeholder Name",
        field: "stakeholderName",
        pinned: "left",
        filter: "agSetColumnFilter",
        autoHeight: true,
        cellStyle: tableCellStyle,
        sortable: true,
        wrapText: true,
        suppressSizeToFit: true,
        menuTabs: ["filterMenuTab"],
        initialWidth: 300,
        cellRenderer: "stakeholderName",
        cellRendererParams: (params: any) => ({
          onClickShareholderName: () =>
            onClickShareholderName(params.data.shareholderId),
        }),
      },
      {
        headerName: "No. of Options",
        field: "reservedPool",
        filter: "agNumberColumnFilter",
        cellStyle: tableCellStyle,
        cellRenderer: "reservedPool",
        sortable: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipReservedPool",
      },
      {
        headerName: "Conversion Ratio",
        field: "ratio",
        filter: "agNumberColumnFilter",
        cellRenderer: "ratio",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Total Shares",
        field: "totalShares",
        filter: "agNumberColumnFilter",
        cellStyle: tableCellStyle,
        cellRenderer: "totalShares",
        sortable: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Notes",
        field: "notes",
        filter: "agTextColumnFilter",
        cellStyle: tableCellStyle,
        cellRenderer: "notes",
        sortable: true,
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Allotment Date",
        field: "allotmentDate",
        filter: "agDateColumnFilter",
        cellRenderer: "allotmentDate",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        hide: true,
      },
    ];

    if (userRoleData[0].role !== "Admin Viewer") {
      columnDef.push({
        headerName: "",
        field: "actions",
        pinned: "right",
        hide: false,
        width: 80,
        maxWidth: 80,
        filter: false,
        menuTabs: [],
        colId: "action-column",
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        cellStyle: { "padding-top": "16px", "line-height": "20px" },
        resizable: false,
        sortable: false,
        editable: false,
        cellRendererParams: ({ value }: { value: any }) => value?.props,
        cellRenderer: CTADropdown,
      });
    }
    return columnDef;
  }, [userRoleData]);

  const gridOptions = {
    suppressRowVirtualisation: true,
    paginationAutoPageSize: false,
    suppressScrollOnNewData: true,
  };
  function handleModifyAction(action: string, data: any) {
    if (action === "Delete") {
      deleteEsopDetail(data.id, {
        onSuccess: () => {
          toast.success("ESOP Stakeholder Deleted Successfully");
        },
        onError: (err: any) => {
          toast.error(
            err?.response?.data?.errorMessage || "Error deleting stakeholder"
          );
        },
      });
    } else if (action === "Add") {
      setDialog({
        open: true,
        mode: "Add",
        data,
      });
    } else if (action === "Update") {
      setDialog({
        open: true,
        mode: "Update",
        data,
      });
    }
  }

  function setPinnedBottomRowData({ api }: { api: any }) {
    const data = api.rowModel.rootNode;
    const filteredData = data.childrenAfterAggFilter.map(
      (element: any) => element.data
    );
    if (filteredData.length === 0) {
      api.setPinnedBottomRowData([]);
      return;
    }

    const totalReservedPool = filteredData?.reduce(
      (accumulator: any, data: any) =>
        accumulator + (data.reservedPoolRaw || 0),
      0
    );
    const totalShares = filteredData?.reduce(
      (accumulator: any, data: any) =>
        accumulator +
        Math.floor((data.reservedPoolRaw || 0) / (data.conversionRatio || 1)),
      0
    );

    api.setPinnedBottomRowData([
      {
        stakeholderName: "Total",
        reservedPool: totalReservedPool?.toLocaleString(currencyType),
        tooltipReservedPool: totalReservedPool?.toLocaleString(currencyType),
        ratio: "",
        totalShares: totalShares?.toLocaleString(currencyType),
        currencyType,
        currencySymbol,
      },
    ]);
  }
  const esopStakeholdersList = useMemo(() => {
    if (!esopPLanDetailsList) return [];
    return esopPLanDetailsList.map((plan) => {
      const shareholder = transactionSwitchStore.shareholders?.find(
        (s: any) => s.id === plan.shareholderId
      );
      return {
        id: plan.id,
        planName: plan.planName,
        reservedPool: plan.reservedPool,
        allocatedPool: plan.allocatedPool,
        unallocatedPool: plan.unallocatedPool,
        planType: plan.planType,
        conversionRatio: plan.conversionRatio,
        shareholderId: plan.shareholderId,
        shareholderName: shareholder?.name || "",
        notes: plan?.notes || "",
        allotmentDate: plan.allotmentDate || "",
      };
    });
  }, [esopPLanDetailsList, shareholderId, transactionSwitchStore.shareholders]);

  const processedStakeholdersList = useMemo(() => {
    if (!esopStakeholdersList) return [];
    try {
      const filterResult = globalFilter(
        esopStakeholdersList,
        globalFilterText,
        ["planName", "shareholderName", "reservedPool", "conversionRatio"]
      );
      const sortResult = sort(
        filterResult,
        sortField?.field,
        sortField?.ascending
      );
      return sortResult;
    } catch (e) {
      return esopStakeholdersList;
    }
  }, [esopStakeholdersList, globalFilterText, sortField]);

  const rowData = useMemo(
    () =>
      processedStakeholdersList?.map((stakeholder) => ({
        id: stakeholder.id,
        planName: stakeholder.planName,
        stakeholderName: stakeholder.shareholderName,
        shareholderId: stakeholder.shareholderId,
        shareholderData: transactionSwitchStore.shareholders?.find(
          (s: any) => s.id === stakeholder.shareholderId
        ),
        reservedPool: stakeholder.reservedPool?.toLocaleString(currencyType),
        reservedPoolRaw: stakeholder.reservedPool,
        tooltipReservedPool:
          stakeholder.reservedPool?.toLocaleString(currencyType),
        ratio: stakeholder.conversionRatio
          ? `${parseFloat(stakeholder.conversionRatio.toFixed(4))}:1`
          : "1:1",
        totalShares: Math.floor(
          stakeholder.reservedPool / (stakeholder.conversionRatio || 1)
        )?.toLocaleString(currencyType),
        conversionRatio: stakeholder.conversionRatio || 1,
        notes: stakeholder?.notes || "",
        allocatedPool: stakeholder.reservedPool,
        unallocatedPool: 0,
        allotmentDate: stakeholder.allotmentDate || "",
        planType: "",
        actions: (
          <CTADropdown
            actions={[
              {
                name: "Update",
                disabled: false,
              },
              {
                name: "Delete",
                disabled: false,
              },
            ]}
            onAction={(action) => {
              handleModifyAction(action.name, stakeholder);
            }}
          />
        ),
      })),
    [processedStakeholdersList, currencyType]
  );

  const handleCellClick = (cellParams: CellClickedEvent<any, any>) => {
    if (
      cellParams.column.getColId() !== "action-column" &&
      cellParams.column.getColId() !== "shareholderName"
    ) {
      const selectedRowIndex = cellParams.node.rowIndex || 0;
      const stakeholder = rowData[selectedRowIndex];
    }
  };

  const openToolPanel = (key: any) => {
    if (key === "columns") {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      }
    } else if (key === "filters") {
      if (gridApi) {
        if (!isFilterOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };

  const [filteredRowData, setFilteredRowData] = useState<any>([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const onAgGridFilterChanged = (grid: any) => {
    const filtersApplied = grid.api.isAnyFilterPresent();
    setIsFilterApplied(filtersApplied);

    const filteredData = grid.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);
    setFilteredRowData(filteredData);
    setPinnedBottomRowData(grid);

    uploadFilterAndColumn();
  };

  return (
    <>
      <HStack className="items-center justify-between py-2 bg-white rounded-md shadow-box">
        <GenericTableHeader
          heading={"ESOP Stakeholders"}
          subHeading={"stakeholders"}
          count={
            isFilterApplied === true ? filteredRowData.length : rowData.length
          }
        />
        <Dialog open={dialog.open} maxWidth="md" fullWidth>
          {(dialog.mode === "Add" || dialog.mode === "Update") && (
            <StakeholderEsopPopup
              mode={dialog.mode as "Add" | "Update"}
              data={dialog.data}
              // onPrimaryAction={() => {
              //   setDialog({ open: false });
              //   toast.success(
              //     `Stakeholder ${
              //       dialog.mode === "Add" ? "added" : "updated"
              //     } successfully`
              //   );
              // }}
              onPrimaryAction={() => {
                setDialog({ open: false });
              }}
              onSecondaryAction={() => setDialog({ open: false })}
              shareholderId={shareholderId}
              shareholderName={shareholderName}
              existingStakeholders={processedStakeholdersList}
            />
          )}
        </Dialog>
        <HStack className="items-center justify-end gap-4 py-2 mr-4">
          <SearchInput
            placeholder={`Search`}
            onChange={(e: any) => {
              gridApi.current.api.setQuickFilter(e.target.value);
            }}
          />
          <IconCTAButton
            value={"Columns"}
            onClick={() => openToolPanel("columns")}
            iconName={"fluent:column-triple-edit-20-regular"}
            className={`px-4 font-medium items-center flex flex-row ${
              isColumnOpen ? "text-orange-501" : "text-gray-400"
            }`}
          />
          <IconCTAButton
            value={"Filters"}
            onClick={() => openToolPanel("filters")}
            iconName={"material-symbols:filter-alt"}
            className={`px-4 font-medium items-center flex flex-row ${
              isFilterOpen ? "text-orange-501" : "text-gray-400"
            }`}
          />
          {userRoleData[0].role !== "Admin Viewer" && (
            <PrimaryCTAButton
              className="h-8 text-xs2 whitespace-nowrap"
              onClick={() => {
                setDialog({
                  open: true,
                  mode: "Add",
                });
              }}
            >
              Add Stakeholder
            </PrimaryCTAButton>
          )}
        </HStack>
      </HStack>

      <HStack className="justify-between w-full">
        <Box
          style={{
            height: (displayedRowCount + 7.5) * 60,
          }}
          className="w-full h-full max-h-full overflow-x-auto ag-theme-material "
        >
          <AgGridReact
            sideBar={agConfigDashboardSideBar}
            onGridReady={(params) => {
              gridApi.current = params;
            }}
            components={componentsRegistery}
            alwaysShowHorizontalScroll
            alwaysMultiSort
            enableCharts={true}
            enableRangeSelection={true}
            animateRows={true}
            defaultColDef={defaultColDef}
            onFilterChanged={onAgGridFilterChanged}
            onRowDataUpdated={setPinnedBottomRowData}
            onColumnEverythingChanged={uploadFilterAndColumn}
            rowData={rowData}
            onCellClicked={handleCellClick}
            columnDefs={columnDefs}
            pagination={false}
            suppressRowTransform={true}
            suppressCopyRowsToClipboard={true}
            suppressCopySingleCellRanges={true}
            suppressCellFocus={true}
            onColumnResized={uploadFilterAndColumn}
            suppressMenuHide={false}
            tooltipShowDelay={1000}
            tooltipInteraction={true}
            rowClass={"border-t border-dashed"}
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
            }
            gridOptions={gridOptions}
            getRowStyle={(params) => {
              if (params.rowIndex % 2 === 0) {
                return { background: "#f8f8f8" };
              } else {
                return { background: "#ffffff" };
              }
            }}
          ></AgGridReact>
        </Box>
      </HStack>
    </>
  );
}

export default EsopStakeholdersAgTable;
